<template>
  <div class="py-4 container-fluid">
    <!-- Filter -->
    <div class="row mb-2">
      <form role="form" @submit.prevent="fetchData">
        <div class="row align-items-end">
          <!-- Date -->
          <div class="col-lg-3 col-md-4 col-sm-6 col-12">
            <label for="date">วันที่</label>
            <VueDatePicker v-model="date" :enableTimePicker="false" />
          </div>

          <div class="col-lg-3 col-md-4 col-sm-6 col-6 mt-3 mt-md-0">
            <argon-button
              fullWidth
              color="success"
              variant="gradient"
              type="submit"
              :disabled="loading"
            >
              <template v-if="loading">กำลังโหลด...</template>
              <template v-else>ค้นหา</template>
            </argon-button>
          </div>
          <div class="col-lg-3 col-md-4 col-sm-6 col-6 mt-3 mt-md-0">
            <argon-button
              fullWidth
              color="danger"
              variant="gradient"
              type="button"
              @click="resetData"
              :disabled="loading"
            >
              <template v-if="loading">กำลังโหลด...</template>
              <template v-else>Reset</template>
            </argon-button>
          </div>
        </div>
      </form>
    </div>

    <!-- Currency Tabs -->
    <div
      class="d-flex flex-wrap justify-content-center align-items-center gap-3 mb-2"
    >
      <div class="currency-tabs">
        <button
          v-for="c in currencyList"
          :key="c"
          type="button"
          class="currency-tab"
          :class="{ active: currency === c }"
          @click="selectCurrency(c)"
        >
          {{ c }}
        </button>
      </div>
    </div>
    <p class="text-center text-sm text-muted mb-4">
      เรทสกุลเงิน:
      <span v-for="(rate, cur) in currencyRates" :key="cur" class="me-2">
        {{ cur }} = {{ rate }}
      </span>
    </p>

    <!-- Summary Cards -->
    <div class="row">
      <div class="col-xl-3 col-md-6 mb-4">
        <MiniStatisticsCard
          title="จำนวนรายการทั้งหมด"
          :value="{ text: formatNumber(current.total), color: 'text-dark' }"
          :description="dateLabel"
          :icon="{
            component: 'ni ni-chart-bar-32',
            background: 'bg-gradient-primary',
            shape: 'rounded-circle',
          }"
        />
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <MiniStatisticsCard
          title="ต่ำกว่าขั้นต่ำ"
          :value="{ text: formatNumber(current.below_min), color: 'text-dark' }"
          :description="dateLabel"
          :icon="{
            component: 'ni ni-fat-remove',
            background: 'bg-gradient-danger',
            shape: 'rounded-circle',
          }"
        />
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <MiniStatisticsCard
          title="ซื้อฟีเจอร์ (ครั้ง)"
          :value="{ text: formatNumber(featureTotalCount), color: 'text-dark' }"
          :description="dateLabel"
          :icon="{
            component: 'ni ni-cart',
            background: 'bg-gradient-success',
            shape: 'rounded-circle',
          }"
        />
      </div>
      <div class="col-xl-3 col-md-6 mb-4">
        <MiniStatisticsCard
          title="ยอดซื้อฟีเจอร์รวม"
          :value="{ text: formatNumber(featureTotalAmount), color: 'text-dark' }"
          :description="dateLabel"
          :icon="{
            component: 'ni ni-money-coins',
            background: 'bg-gradient-warning',
            shape: 'rounded-circle',
          }"
        />
      </div>
    </div>

    <!-- Bet Range Table -->
    <div class="col-md-12 mt-2">
      <h5 class="mb-2 text-center fw-bold">📊 ระดับการเดิมพัน ({{ currency }})</h5>
      <div class="table-responsive">
        <table
          class="table table-striped table-flush align-items-center text-dark mb-0"
          style="text-align-last: center"
        >
          <thead>
            <tr>
              <th>ช่วงเดิมพัน</th>
              <th>จำนวน</th>
              <th>สัดส่วน</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="3">🔄 กำลังโหลด...</td>
            </tr>
            <tr v-else-if="current.ranges.length === 0">
              <td colspan="3">ไม่มีข้อมูล</td>
            </tr>
            <tr v-else v-for="r in current.ranges" :key="r.range">
              <td>{{ r.range }}</td>
              <td class="text-end">{{ formatNumber(r.count) }}</td>
              <td class="text-end">{{ percent(r.count, current.total) }}%</td>
            </tr>
            <tr v-if="!loading && current.below_min > 0" class="fw-bold">
              <td>ต่ำกว่าขั้นต่ำ</td>
              <td class="text-end">{{ formatNumber(current.below_min) }}</td>
              <td class="text-end">
                {{ percent(current.below_min, current.total) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Feature Buy Table -->
    <div class="col-md-12 mt-4">
      <h5 class="mb-2 text-center fw-bold">🛒 ซื้อฟีเจอร์</h5>
      <div v-if="featureGroups.length === 0 && !loading" class="text-center">
        ไม่มีข้อมูล
      </div>
      <div
        v-for="group in featureGroups"
        :key="group.currency"
        class="table-responsive mb-3"
      >
        <p class="mb-1 fw-bold text-center">สกุลเงิน: {{ group.currency }}</p>
        <table
          class="table table-striped table-flush align-items-center text-dark mb-0"
          style="text-align-last: center"
        >
          <thead>
            <tr>
              <th>ราคา</th>
              <th>จำนวนครั้ง</th>
              <th>ยอดรวม</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in group.prices" :key="p.price">
              <td class="text-end">{{ formatNumber(p.price) }}</td>
              <td class="text-end">{{ formatNumber(p.count) }}</td>
              <td class="text-end">{{ formatNumber(p.total_amount) }}</td>
            </tr>
            <tr class="fw-bold">
              <td>รวม</td>
              <td class="text-end">{{ formatNumber(group.total_count) }}</td>
              <td class="text-end">{{ formatNumber(group.total_amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Top Users Table -->
    <div class="col-md-12 mt-4">
      <h5 class="mb-2 text-center fw-bold">🏆 ผู้เล่นเดิมพันสูงสุด</h5>
      <div class="table-responsive">
        <table
          class="table table-striped table-flush align-items-center text-dark mb-0"
          style="text-align-last: center"
        >
          <thead>
            <tr>
              <th>อันดับ</th>
              <th>ผู้เล่น</th>
              <th>Agent</th>
              <th>สกุลเงิน</th>
              <th>เดิมพันสูงสุด</th>
              <th>เกม</th>
              <th>เดิมพันรวม (THB)</th>
              <th>ระบบได้กำไร/ขาดทุน (THB)</th>
              <th>วันที่</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="9">🔄 กำลังโหลด...</td>
            </tr>
            <tr v-else-if="topUsers.length === 0">
              <td colspan="9">ไม่มีข้อมูล</td>
            </tr>
            <tr v-else v-for="u in topUsers" :key="u.rank">
              <td>{{ u.rank }}</td>
              <td>{{ u.username }}</td>
              <td>{{ u.agent }}</td>
              <td>{{ u.currency }}</td>
              <td class="text-end">{{ formatNumber(u.bet_amount) }}</td>
              <td>{{ u.game_code }} {{ u.game }}</td>
              <td class="text-end">{{ formatNumber(u.total_bet_amount_thb) }}</td>
              <td class="text-end">
                {{ formatNumber(-(u.total_win_loss_thb ?? 0)) }}
              </td>
              <td>{{ formatDateTime(u.date) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import VueDatePicker from "@vuepic/vue-datepicker";
import "flatpickr/dist/flatpickr.css";
import "@vuepic/vue-datepicker/dist/main.css";

import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const API_BASE = "https://dashboard-api.linkstudio.io/api/v1/game/control";

const date = ref(moment().format("YYYY-MM-DD"));
const loading = ref(false);
const currency = ref("ALL");
const resp = ref(null);

// ---- currency ----
const currencyList = computed(() => {
  const list = ["ALL"];
  (resp.value?.by_currency ?? []).forEach((c) => {
    if (c.currency && !list.includes(c.currency)) list.push(c.currency);
  });
  return list;
});

const currencyRates = computed(() => resp.value?.currency_rates ?? {});

// ข้อมูลระดับเดิมพันตาม currency ที่เลือก
const current = computed(() => {
  if (!resp.value) return { ranges: [], below_min: 0, total: 0 };
  if (currency.value === "ALL") {
    return {
      ranges: resp.value.ranges ?? [],
      below_min: resp.value.below_min ?? 0,
      total: resp.value.total ?? 0,
    };
  }
  const found = (resp.value.by_currency ?? []).find(
    (c) => c.currency === currency.value
  );
  return found
    ? {
        ranges: found.ranges ?? [],
        below_min: found.below_min ?? 0,
        total: found.total ?? 0,
      }
    : { ranges: [], below_min: 0, total: 0 };
});

// feature buy ตาม currency (ALL = ทุกสกุล)
const featureGroups = computed(() => {
  const fb = resp.value?.feature_buy ?? [];
  if (currency.value === "ALL") return fb;
  return fb.filter((f) => f.currency === currency.value);
});
const featureTotalCount = computed(() =>
  featureGroups.value.reduce((s, g) => s + (g.total_count ?? 0), 0)
);
const featureTotalAmount = computed(() =>
  featureGroups.value.reduce((s, g) => s + (g.total_amount ?? 0), 0)
);

const topUsers = computed(() => resp.value?.top_users ?? []);
const dateLabel = computed(() => formatDate(resp.value?.date));

// ---- helpers ----
function formatNumber(n) {
  const num = Number(n ?? 0);
  return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
function percent(part, total) {
  if (!total) return "0.00";
  return ((Number(part) / Number(total)) * 100).toFixed(2);
}
function formatDate(d) {
  if (!d) return "-";
  return moment(d).format("YYYY-MM-DD");
}
function formatDateTime(d) {
  if (!d) return "-";
  return moment(d).format("YYYY-MM-DD HH:mm");
}

// ---- actions ----
function selectCurrency(c) {
  currency.value = c;
}

function resetData() {
  date.value = moment().format("YYYY-MM-DD");
  currency.value = "ALL";
  fetchData();
}

async function fetchData() {
  loading.value = true;
  const d = moment(date.value).format("YYYY-MM-DD");
  try {
    const res = await axios.get(`${API_BASE}/dashboard/bet-range/${d}`);
    resp.value = res.data;
    // ถ้า currency ที่เลือกไว้ไม่มีในชุดข้อมูลใหม่ → กลับไป ALL
    if (!currencyList.value.includes(currency.value)) currency.value = "ALL";
  } catch (err) {
    console.error(err);
    Swal.fire({
      icon: "error",
      title: "เกิดข้อผิดพลาด",
      text:
        err.response?.data?.message || err.message || "ไม่สามารถดึงข้อมูลได้",
    });
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.currency-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: #f0f2f5;
  border-radius: 12px;
}
.currency-tab {
  border: none;
  background: transparent;
  color: #67748e;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 22px;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.currency-tab:hover {
  color: #2dce89;
}
.currency-tab.active {
  background: linear-gradient(310deg, #2dce89 0%, #2dcecc 100%);
  color: #fff;
  box-shadow: 0 3px 6px rgba(45, 206, 137, 0.4);
}
.table td.text-end {
  text-align: right !important;
  text-align-last: right !important;
}
</style>
